// src/components/CardComponent.js
import React from "react";
import { useNavigate } from "react-router-dom";
import "../App.css";
import { getStatusMeta, parseBool } from "../scripts/status";
import { StickyNote, Plus } from "lucide-react";

function pickVin(r) {
  return r.chassis_vin || r.body_vin || "";
}

function isTruthyFlag(v) {
  const s = String(v ?? "").toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "y";
}

function getUnitKind(record) {
  const isChassis = isTruthyFlag(record.is_chassis);
  const isBody = isTruthyFlag(record.is_body);
  const isAttachment = isTruthyFlag(record.is_attachment);
  const isEquipment = isTruthyFlag(record.is_equipment);

  if (isChassis || isBody || isAttachment || isEquipment) {
    const parts = [];
    if (isChassis) parts.push("Chassis");
    if (isBody) parts.push("Body");
    if (isAttachment) parts.push("Attachment");
    if (isEquipment) parts.push("Equipment");
    return parts.join(" + ");
  }

  const hasChassis = !!(record.chassis_vin && String(record.chassis_vin).trim());
  const hasBody = !!(record.body_vin && String(record.body_vin).trim());
  if (hasChassis && hasBody) return "Body + Chassis";
  if (hasChassis) return "Chassis";
  if (hasBody) return "Body";
  return "";
}

function NotesBadge({ total = 0, unread = 0, onClick }) {
  if (!total) return null;

  const hot = unread > 0;

  return (
    <span
      role="button"
      tabIndex={0}
      title={hot ? `${unread} new note(s)` : `${total} note(s)`}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          onClick?.();
        }
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 900,
        cursor: "pointer",
        userSelect: "none",
        border: `1px solid ${hot ? "rgba(255,0,0,0.45)" : "rgba(0,0,0,0.10)"}`,
        background: hot ? "rgba(255,0,0,0.16)" : "rgba(0,0,0,0.06)",
        color: hot ? "#b00000" : "#000",
      }}
    >
      <StickyNote size={12} />
      <span>{total}</span>
    </span>
  );
}

function AddNoteButton({ onClick }) {
  return (
    <span
      role="button"
      tabIndex={0}
      title="Add note"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          onClick?.();
        }
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 900,
        cursor: "pointer",
        userSelect: "none",
        border: "1px solid rgba(0,0,0,0.10)",
        background: "rgba(0,0,0,0.06)",
        color: "#000",
      }}
    >
      <Plus size={12} />
      <span>Note</span>
    </span>
  );
}

export default function CardComponent({
  record,
  table = "all_inventory",
  notesTotal = 0,
  notesUnread = 0,
  onOpenNotes,
  onAddNote,
  actions = [],
  onCreateMove,
  onDeleteWithPassword,
}) {
  const nav = useNavigate();
  const [showActions, setShowActions] = React.useState(false);

  const meta = getStatusMeta(record);

  const vin = pickVin(record);
  const rowo = record.rowo || "";
  const stock = record.stock_po || record.sna_stock_po || record.stock_num || "";

  const customer = record.customer || record.owner || "";
  const desc =
    record.make_model ||
    record.chassis_make_model ||
    record.body_make_model ||
    record.body_model_size ||
    record.description ||
    record.desc ||
    "";

  const category = record.unit_category || "";
  const status = record.unit_status || record["unit_status.1"] || "";
  const alert = parseBool(record.alert ?? record["alert.1"]);
  const kind = getUnitKind(record);

  const goDetail = () => nav(`/detail/${table}/${record.id}`);
  const defaultActions = [
    { label: "View Details", onClick: () => goDetail() },
    ...(onCreateMove ? [{ label: "Create Move", onClick: () => onCreateMove(record) }] : []),
    ...(onDeleteWithPassword
      ? [{ label: "Delete", variant: "danger", onClick: () => onDeleteWithPassword(record) }]
      : []),
  ];
  const allActions = actions.length ? actions : defaultActions;

  return (
    <div
      className="card-button card-text-black"
      style={{
        background: meta.bg,
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
        wordBreak: "break-word",
      }}
      role="button"
      tabIndex={0}
      onClick={() => setShowActions((v) => !v)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setShowActions((v) => !v);
        }
      }}
    >
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 5 }}>
        <div className="row" style={{ gap: 5, alignItems: "center" }}>
          <div className="pill" style={{ background: meta.bg }}>
            {meta.label}
          </div>
          <div className="muted">ID #{record.id}</div>
        </div>
        <div className="row" style={{ gap: 5, alignItems: "center" }}>
          <NotesBadge total={notesTotal} unread={notesUnread} onClick={onOpenNotes} />
          <AddNoteButton onClick={onAddNote} />
          {allActions.length > 0 && (
            <button
              type="button"
              className="btn-outline"
              style={{ padding: "4px 8px", fontSize: 11 }}
              onClick={(e) => {
                e.stopPropagation();
                setShowActions((v) => !v);
              }}
            >
              {showActions ? "Hide" : "Actions"}
            </button>
          )}
        </div>
      </div>

      <div className="row" style={{ gap: 5, marginTop: 4, flexWrap: "wrap" }}>
        {kind && (
          <div className="pill" style={{ background: "#dd8828ff", color: "#000000ff" }}>
            {kind}
          </div>
        )}
        {category && (
          <div className="pill" style={{ background: "#dd8828ff", color: "#000000ff" }}>
            {category}
          </div>
        )}
        {status && <div className="pill" style={{ background: "#eee" }}>{status}</div>}
        {alert && (
          <div className="pill" style={{ background: "#ff0000", color: "#fff" }}>
            ALERT
          </div>
        )}
      </div>

      <div style={{ marginTop: 4 }}>
        <div style={{ fontWeight: 700, marginBottom: 2 }}>
          {customer ? `${customer} - ` : ""}{desc || "-"}
        </div>
        <div className="row" style={{ flexWrap: "wrap", gap: 8 }}>
          <div>
            <b>RO/WO:</b> {rowo || "-"}
          </div>
          <div>
            <b>Stock PO:</b> {stock || "-"}
          </div>
          <div>
            <b>VIN:</b> {vin || "-"}
          </div>
        </div>
      </div>

      {showActions && allActions.length > 0 && (
        <div
          className="row"
          style={{
            marginTop: 10,
            gap: 8,
            flexWrap: "wrap",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {allActions.map((a, idx) => (
            <button
              key={idx}
              className={a.variant === "danger" ? "btn-outline" : "btn"}
              style={{ fontSize: 12, padding: "6px 10px" }}
              onClick={(e) => {
                e.stopPropagation();
                a.onClick?.(record);
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
